import { CheckboxGroup, FileUpload, FormError, FormRow, Icon, OrderableList, Tooltip } from '@components/ui';
import { GeneralHelper } from '@helpers';
import { notionStore } from '@stores/notion.store';
import { DataCollectionInputs, DataCollectionOptions } from '@types';
import DOMPurify from 'dompurify';
import { observer } from 'mobx-react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import styles from '../../Steps.module.scss';

export type TabularMLStep2Options = Pick<
  DataCollectionOptions,
  'protectedGroups' | 'performanceMetrics' | 'protectedGroups'
>;

export type TabularMLStep2Data = Pick<DataCollectionInputs, 'protectedGroupsRanked' | 'protectedGroupsFile'>;

interface TabularMLStep2Props {
  options: TabularMLStep2Options;
}

export const TabularMLStep2 = observer(({ options }: TabularMLStep2Props) => {
  const { values } = notionStore;

  const {
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = useFormContext<TabularMLStep2Data>();

  const [protectedGroupsFile, protectedGroupsRanked] = watch(['protectedGroupsFile', 'protectedGroupsRanked']);
  const rankedGroupIds = (protectedGroupsRanked ?? []) as number[];
  const rankedGroupIdsAsStrings = rankedGroupIds.map(id => String(id));

  return (
    <ol className={styles.list}>
      <FormRow>
        <div>
          <li className={styles.questionLabel}>
            <div>
              <span
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(values?.step2Question1Text),
                }}
              />
              {values?.step2Question1Info && (
                <Tooltip
                  content={
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(values?.step2Question1Info, { ADD_ATTR: ['target'] }),
                      }}
                    />
                  }
                >
                  <Icon name="question-mark" />
                </Tooltip>
              )}
            </div>
          </li>

          <div>
            <div className={styles.optionsSection}>
              <CheckboxGroup
                options={options.protectedGroups}
                defaultSelected={rankedGroupIdsAsStrings}
                onSelect={labels => {
                  // validation
                  if (labels.length === 0) {
                    setError('protectedGroupsRanked', {
                      message: 'Please select at least one option',
                      type: 'required',
                    });
                  } else {
                    clearErrors('protectedGroupsRanked');
                  }
                  // add last checked group into the ranked list (on the last place)
                  if (labels.length > rankedGroupIds.length) {
                    const lastLabel = labels[labels.length - 1];
                    const lastId = Number(lastLabel);
                    setValue('protectedGroupsRanked', [...rankedGroupIds, lastId]);
                  } else {
                    const selectedIds = new Set(labels.map(label => Number(label)));
                    setValue(
                      'protectedGroupsRanked',
                      rankedGroupIds.filter(id => selectedIds.has(id)),
                    );
                  }
                }}
                inputProps={{
                  ...register('protectedGroupsRanked', {
                    required: 'Please select at least one option',
                  }),
                }}
              />

              <FormError error={errors.protectedGroupsRanked?.message} />
            </div>
            {rankedGroupIds.length > 0 && (
              <div className={styles.optionsSection}>
                <p className={styles.sublabel}>
                  <div>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(values?.step2Question2Text),
                      }}
                    />
                    {values?.step2Question2Info && (
                      <Tooltip
                        content={
                          <div
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(values?.step2Question2Info, { ADD_ATTR: ['target'] }),
                            }}
                          />
                        }
                      >
                        <Icon name="question-mark" />{' '}
                      </Tooltip>
                    )}
                  </div>
                </p>
                <OrderableList
                  list={rankedGroupIds
                    ?.map(groupId => options.protectedGroups.find(group => Number(group.id) === groupId)!)
                    ?.filter(Boolean)}
                  onReorder={newRanking => {
                    setValue(
                      'protectedGroupsRanked',
                      newRanking.map(group => Number(group.id)),
                    );
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </FormRow>
      <FormRow>
        <li className={styles.questionLabel}>
          <div>
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(values?.step2Question3Text),
              }}
            />
            {values?.step2Question3Info && (
              <Tooltip
                content={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(values?.step2Question3Info, { ADD_ATTR: ['target'] }),
                    }}
                  />
                }
              >
                <Icon name="question-mark" />{' '}
              </Tooltip>
            )}
          </div>
        </li>
        <div className={styles.filedropWrapper}>
          <p className={styles.fileLabel}>Protected groups</p>
          <FileUpload
            fileTypeDescription="CSV (Protected groups)"
            name="protectedGroupsFile"
            inputTestId="protected-groups-file-upload"
            control={control}
            rules={{ required: 'Please upload protected groups file' }}
            file={protectedGroupsFile}
            acceptTypes={{
              'text/csv': [],
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
              'application/vnd.ms-excel': [],
            }}
            onFileChanged={file => {
              if (!file) {
                setError('protectedGroupsFile', { message: 'Please upload protected groups file' });
              } else {
                clearErrors('protectedGroupsFile');
              }
              setValue('protectedGroupsFile', file);
            }}
            onFileDownload={GeneralHelper.downloadFile}
          />
        </div>
      </FormRow>
    </ol>
  );
});
